import clsx from "clsx";
import { ChevronRight, Clock, FileText, Fuel, Mountain, Swords, Wrench } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type {
  Logistics,
  LogisticsAssumptions,
  LogisticsMaintenance,
  MaintenanceFamily,
  MaintenancePhase,
  MaintenancePosture,
  MaterielFamily,
  UnitCapops,
} from "../../api/types";
import { designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Collapsible } from "../../components/Collapsible";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { PanelGroup, PanelRow, StatTile } from "../../components/panel";
import { Motion } from "../../components/presence";
import { Badge, Button, FloatingPanel } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { Waiting } from "../../components/Waiting";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, k, t } from "../../i18n/i18n";
import {
  CAPOPS_FULL,
  colorTone,
  dtoColor,
  DTO_FULL,
  dtoReading,
  FLEET_KEYS,
  familyLabel,
  GREEN_ABOVE,
  limitText,
  maintenanceColor,
  percentText,
} from "../../maintenance";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { useUi } from "../../state/ui";
import { OpenMissionDataButton } from "../openMissionData";
import { AssumptionsBadge, customOf, ResetAssumptions, Slider, type SliderSpec, useSaveAssumptions } from "../logistics/assumptions";
import { mobilityLabel, SituationLine, useLogistics } from "../logistics/LogisticsPanel";
import { CapopsBadge, CapopsStatusIcon, DtoBadge, DtoCurve, DtoStatusIcon, initialFamily, LegLines } from "./DtoCurve";
import { FleetTable, type Preview, previewed } from "./FleetTable";

/** Maintenance S4 (vue « Maintenance », chantier « DTO / CAPOPS », DTO requise par parc du 04/11/2026) : la DTO
 *  (disponibilité technique opérationnelle) par parc de matériel, de T0 à T3, jugée contre la DTO requise du parc à
 *  chaque temps (réglée par curseurs parc × temps, `FleetTable` ; une DTO à la requise ou au-dessus n'est jamais une
 *  alerte), et la CAPOPS des unités (maillon le plus faible du personnel, des matériels et des munitions, aux couleurs
 *  de l'iPad). Indicateurs de nature différente du ravitaillement (vue Logistique, UE, UF, drones), d'où une vue à
 *  part, qui lit pourtant la même réponse du serveur (`useLogistics` : même cache, même invalidation). « Ouvrir la maintenance » d'un constat
 *  de cohérence ou d'une fiche d'unité (`focusMaintenance(family)`) désigne la famille. */

/** Hypothèses de la chute de DTO (avaries, dommages de combat) et de la maintenance opérationnelle (remises en état). */
const SLIDERS: SliderSpec[] = [
  { key: "failures_per_day", label: k("Taux d'avaries par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "combat_damage_per_day", label: k("Dommages de combat par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "failures_per_100km", label: k("Avaries pour 100 km"), digits: 1, unit: k("% du parc") },
  { key: "repairs_per_day", label: k("Remises en état par jour (NTI1-NTI2)"), digits: 0, unit: k("% des matériels indisponibles") },
];

export const MAINTENANCE_KEYS: readonly (keyof LogisticsAssumptions)[] = [...SLIDERS.map((s) => s.key), ...FLEET_KEYS];
const RATE_KEYS: readonly (keyof LogisticsAssumptions)[] = SLIDERS.map((s) => s.key);

/** Posture et coefficients d'engagement du calcul (`maintenance.py` : CONTACT_ENGAGEMENT, REAR_ENGAGEMENT,
 *  DESTROYED_SHARE) : au contact, avaries et dommages de combat × 1, × 0,85 ou × 0,75 ; hors contact, avaries × 0,25 et
 *  aucun dommage de combat ; un quart des dommages de combat sont des pertes définitives. */
const POSTURE: Record<MaintenancePosture, string> = {
  offensive: k(
    "Offensive : au contact, taux d'avaries et de dommages de combat pleins (× 1) ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
  defensive: k(
    "Défensive : au contact, taux d'avaries et de dommages de combat × 0,75 ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
  mixed: k(
    "Mixte : au contact, taux d'avaries et de dommages de combat × 0,85 ; hors contact, avaries au quart (× 0,25), sans dommages de combat ; un quart des dommages de combat sont des pertes définitives.",
  ),
};

/** « T1 5 h, T2 10 h, T3 21 h ». */
const hoursText = (hours: LogisticsMaintenance["hours"]) =>
  (["T1", "T2", "T3"] as const).map((p) => t("{phase} {hours} h", { phase: p, hours: formatNumber(Math.round(hours[p])) })).join(", ");

/** « T2 : 60 % » quand tous les parcs ont la même DTO requise au temps ; « T2 : 55 à 65 % selon le parc » sinon. */
function requiredSpread(families: MaintenanceFamily[], phase: MaintenancePhase): string {
  const values = families.map((f) => Math.round(f.required[phase]));
  const low = Math.min(...values);
  const high = Math.max(...values);
  return low === high
    ? t("DTO requise en {phase} : {n} %", { phase, n: low })
    : t("DTO requise en {phase} : {low} à {high} % selon le parc", { phase, low, high });
}

/** CAPOPS d'une unité de T1 à T3, le temps affiché en gras : « T1 95 % · T2 82 % · T3 71 % ». */
function CapopsTrend({ phases, phase }: { phases: Record<MaintenancePhase, UnitCapops>; phase: MaintenancePhase }) {
  return (
    <span className="tabular-nums">
      {(["T1", "T2", "T3"] as const).map((p, i) => (
        <span key={p} className={clsx(p === phase && "font-semibold text-ink")}>
          {i > 0 && " · "}
          {p} {percentText(phases[p].capops)}
        </span>
      ))}
    </span>
  );
}

/** Le cœur de la vue, sans la requête (essais). D'abord la CAPOPS des unités (demande d'Alexis du 04/11/2026 :
 *  « Generex-web étant utilisé en premier lieu par des tacticiens, soutenus par le soutien, on parle prioritairement
 *  de CAPOPS, puis de DTO, seulement pour les techniciens ») : synthèse, unités les plus affaiblies en tête, ce qui
 *  les limite et leur évolution de T1 à T3. Puis, replié par défaut, le « Détail technique : DTO par parc » (DTO
 *  par parc, courbe, curseurs de la DTO de départ et de la DTO requise `fleets`, taux `rates`), ouvert d'office
 *  quand « Ouvrir la maintenance » désigne un parc. `preview` : curseurs en cours de réglage, montrés aussitôt
 *  (couleurs, paliers, courbe) avant la réponse du serveur ; `chosen` / `onChoose` : parc montré par la courbe,
 *  partagé avec le tableau des curseurs ; `technicalOpen` : détail technique ouvert au départ (essais). */
export function MaintenanceBody({
  data,
  phase,
  preview = {},
  chosen: chosenProp,
  onChoose,
  fleets,
  rates,
  technicalOpen = false,
}: {
  data: Logistics;
  phase: MaintenancePhase;
  preview?: Preview;
  chosen?: MaterielFamily | null;
  onChoose?: (family: MaterielFamily) => void;
  /** Tableau des curseurs parc × temps, sous la courbe, dans le détail technique. */
  fleets?: ReactNode;
  /** Taux d'avaries et de remises en état, à la fin du détail technique. */
  rates?: ReactNode;
  technicalOpen?: boolean;
}) {
  const raw = data.maintenance;
  const m = raw ? previewed(raw, data.assumptions, preview) : raw;
  const focus = useUi((s) => s.maintenanceFocus);
  const focusMaintenance = useUi((s) => s.focusMaintenance);
  const [own, setOwn] = useState<MaterielFamily | null>(() => (m ? initialFamily(m, focus) : null));
  const [technical, setTechnical] = useState(technicalOpen || Boolean(focus));
  const chosen = chosenProp !== undefined && chosenProp !== null ? chosenProp : own;
  const setChosen = (family: MaterielFamily) => {
    setOwn(family);
    onChoose?.(family);
  };

  // Parc désigné par « Ouvrir la maintenance » (constat de cohérence, fiche d'unité) : le détail technique s'ouvre,
  // le parc est choisi, amené à l'écran et mis en surbrillance (fondu), puis oublié. La recherche de la cible n'est
  // annulée qu'au démontage : `focusMaintenance(null)` relance l'effet aussitôt et ne doit pas l'interrompre.
  const cancel = useRef<(() => void) | null>(null);
  useEffect(() => () => cancel.current?.(), []);
  useEffect(() => {
    if (!focus || !m) return;
    if (m.families.some((f) => f.family === focus)) {
      setTechnical(true);
      setChosen(focus);
      cancel.current?.();
      cancel.current = designateSoon(`[data-maintenance-family="${focus}"]`, "ring");
    }
    focusMaintenance(null);
  }, [focus, m]);

  if (!m || !m.families.length) return null;
  const current = m.families.find((f) => f.family === chosen) ?? m.families[0]!;
  const lowest = m.lowest;
  const names = new Map(data.lines.map((l) => [l.key, l.name]));
  const units = Object.entries(m.units)
    .flatMap(([key, phases]) => (phases[phase] && names.has(key) ? [{ key, name: names.get(key)!, phases, capops: phases[phase] }] : []))
    .sort((a, b) => a.capops.capops - b.capops.capops || a.name.localeCompare(b.name));
  const weakest = units[0];
  const weakened = units.filter((u) => Math.round(u.capops.capops) <= GREEN_ABOVE).length;
  const below = m.families.filter((f) => f.below);
  return (
    <>
      {units.length > 0 && (
        <PanelGroup title={t("CAPOPS des unités · temps {phase}", { phase })} count={units.length}>
          <div className="space-y-2 px-4 pb-1">
            <p className="text-meta leading-snug text-ink-soft">
              <strong className="font-semibold text-ink">{t(CAPOPS_FULL)}</strong>
              {t(" : aptitude de l'unité à remplir sa mission, la plus faible du personnel, des matériels et des munitions, en % du potentiel initial.")}
            </p>
            {weakest && (
              <div className="grid grid-cols-2 gap-2">
                <StatTile
                  icon={<CapopsStatusIcon percent={weakest.capops.capops} size={12} />}
                  value={percentText(weakest.capops.capops)}
                  tone={colorTone(maintenanceColor(weakest.capops.capops))}
                  label={t("CAPOPS la plus basse en {phase}", { phase })}
                  hint={unitDesignation(weakest.name)}
                />
                <StatTile
                  value={formatNumber(weakened)}
                  unit={t("sur {n}", { n: formatNumber(units.length) })}
                  tone={weakened ? "warn" : "neutral"}
                  label={t("Unités à 80 % ou moins")}
                  hint={weakened ? t("CAPOPS orange ou rouge") : t("Toutes au-dessus de 80 %")}
                />
              </div>
            )}
          </div>
          <ul className="space-y-px">
            {units.map((u) => {
              const limit = limitText(u.capops);
              return (
                <li key={u.key}>
                  <PanelRow
                    title={<span className="font-semibold">{unitDesignation(u.name)}</span>}
                    detail={
                      <>
                        <span className="block">{limit ? limit.charAt(0).toUpperCase() + limit.slice(1) : t("Rien ne la limite")}</span>
                        <span className="block text-ink-muted">
                          <CapopsTrend phases={u.phases} phase={phase} />
                        </span>
                      </>
                    }
                    state={<CapopsBadge percent={u.capops.capops} />}
                  />
                </li>
              );
            })}
          </ul>
        </PanelGroup>
      )}

      <Collapsible
        flush
        open={technical}
        onOpenChange={setTechnical}
        title={t("Détail technique : DTO par parc")}
        trailing={
          below.length ? (
            <Badge tone="warn">{t("{n} sous la DTO requise", { n: formatNumber(below.length) })}</Badge>
          ) : (
            <span className="text-meta text-ink-muted">{t("Pour le soutien")}</span>
          )
        }
      >
        <PanelGroup title={t("DTO par parc de matériel · temps {phase}", { phase })} className="border-t border-line">
          <div className="space-y-2 px-4 pb-1">
            <p className="text-meta leading-snug text-ink-soft">
              <strong className="font-semibold text-ink">{t(DTO_FULL)}</strong>
              {t(" : part du parc techniquement disponible, cause technique de la CAPOPS. Elle chute avec les avaries et les dommages de combat, remonte avec les remises en état et se juge contre la DTO requise de chaque parc à chaque temps.")}
            </p>
            <div className="space-y-1.5">
              <SituationLine icon={<Wrench size={12} />}>{requiredSpread(m.families, phase)}</SituationLine>
              {m.reduced && (
                <SituationLine icon={<Clock size={12} />}>
                  {t("Opération de plus de {days} jours : la chute de DTO est ramenée à cette durée de référence (taux journaliers divisés d'autant).", {
                    days: formatNumber(m.reference_days),
                  })}
                </SituationLine>
              )}
            </div>
            {lowest && (
              <div className="grid grid-cols-2 gap-2">
                <StatTile
                  icon={<DtoStatusIcon percent={lowest.dto} required={lowest.required} size={12} />}
                  value={percentText(lowest.dto)}
                  tone={colorTone(dtoColor(lowest.dto, lowest.required))}
                  label={t("DTO la plus en retard en {phase}", { phase })}
                  hint={familyLabel(lowest.family)}
                />
                <StatTile value={percentText(lowest.required)} label={t("DTO requise en {phase}", { phase })} hint={t("À la requise ou au-dessus : conforme")} />
              </div>
            )}
          </div>
          <ul className="space-y-px">
            {m.families.map((f) => {
              const value = f.dto[phase];
              const need = f.required[phase];
              return (
                <li key={f.family}>
                  <PanelRow
                    data-maintenance-family={f.family}
                    icon={<DtoStatusIcon percent={value} required={need} />}
                    title={familyLabel(f.family)}
                    detail={
                      <>
                        {t("{n} véhicules", { n: formatNumber(Math.round(f.vehicles)) })}
                        <span> · {t("requise {n} %", { n: Math.round(need) })}</span>
                        {f.below ? (
                          <span className="text-warn"> · {t("sous la DTO requise dès {phase}", { phase: f.below })}</span>
                        ) : (
                          <span> · {dtoReading(value, need)}</span>
                        )}
                      </>
                    }
                    state={<DtoBadge percent={value} required={need} />}
                    selected={f.family === current.family}
                    onClick={() => setChosen(f.family)}
                    clickTitle={t("Voir l'évolution de T0 à T3")}
                  />
                </li>
              );
            })}
          </ul>
          {/* Changement de parc : la courbe et ses lignes reviennent en fondu. */}
          <Motion origin="fade" key={current.family} className="space-y-2 px-4 pt-2 pb-1">
            <p className="text-meta leading-snug font-medium text-ink-muted">
              {t("Évolution de T0 à T3 · {family}", { family: familyLabel(current.family) })}
            </p>
            <DtoCurve dto={current.dto} required={current.required} phase={phase} />
            <LegLines family={current} hours={m.hours} />
            {m.severity > 1.005 && (
              <p className="text-meta leading-snug text-ink-muted">
                {t("Taux d'avaries majoré par le terrain et la météo : × {n}.", { n: formatNumber(m.severity, { maximumFractionDigits: 2 }) })}
              </p>
            )}
          </Motion>
        </PanelGroup>
        {fleets}
        {rates}
      </Collapsible>
    </>
  );
}

export function MaintenancePanel() {
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const setView = useUi((s) => s.setView);
  const { query, key, opo, complete, centac } = useLogistics(exerciseId, phase);
  const { save, reverts } = useSaveAssumptions(exerciseId, phase, opo, query, key);
  const data = query.data;
  const m = data?.maintenance;
  const custom = data ? customOf(data, RATE_KEYS) : false;
  // Curseurs parc × temps en cours de réglage : montrés aussitôt, oubliés dès que le serveur répond (ou refuse).
  const [preview, setPreview] = useState<Preview>({});
  const [chosen, setChosen] = useState<MaterielFamily | null>(null);
  useEffect(() => setPreview({}), [data?.updated_at, reverts]);

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.maintenance.icon}
      title={t("CAPOPS / DTO")}
      actions={<HelpButton screen="maintenance" buttonSize="sm" size={16} />}
    >
      {!exerciseId || !complete ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "La vue suit, de T1 à T3, la CAPOPS (capacité opérationnelle) des unités du dispositif affiché sur la carte et, en détail technique, la DTO (disponibilité technique opérationnelle) de chaque parc de matériel.",
              )}
            </p>
            <p className="mt-2">
              {!exerciseId ? t("Pour suivre la maintenance, créez d'abord le dossier.") : t("Complétez les Données initiales.")}
            </p>
          </EmptyState>
        </Motion>
      ) : query.isError ? (
        <div className="px-4 py-4">
          {zoneRefusalOf(query.error) ? (
            <ZoneWaitCallout message={query.error.message} error={query.error} />
          ) : (
            <Callout tone="danger">{query.error.message}</Callout>
          )}
        </div>
      ) : !data ? (
        <div className="flex items-center gap-2 px-4 py-6 text-ui text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Calcul de la maintenance…")}</Waiting>}
        </div>
      ) : centac ? (
        <Motion origin="fade" className="px-4 py-4">
          <Callout
            tone="info"
            action={
              <Button variant="secondary" size="sm" onClick={() => setView("order")}>
                <FileText size={14} /> {t("Ouvrir l'ordre")}
              </Button>
            }
          >
            {t("En rotation CENTAC, la CAPOPS vient des résultats de chaque OPO de la rotation : la maintenance n'est pas estimée.")}
          </Callout>
        </Motion>
      ) : !data.totals || !m ? (
        <Motion origin="fade">
          <p className="flex items-start gap-2 border-b border-line px-4 py-3.5 text-ui leading-relaxed text-ink-soft">
            <Swords size={14} className="mt-0.5 shrink-0 text-ink-muted" />
            {data.empty === "no_ami"
              ? t("Aucune unité amie n'est placée au temps {phase}.", { phase: data.phase })
              : t("Aucun ennemi n'est placé au temps {phase} : l'engagement des unités se mesure face au dispositif ennemi.", {
                  phase: data.phase,
                })}
          </p>
        </Motion>
      ) : (
        <Motion origin="fade" className={clsx("transition-opacity", query.isFetching && "opacity-80")}>
          <PanelGroup title={t("En direct depuis la carte · temps {phase}", { phase: data.phase })} flush>
            <div className="space-y-1.5 px-4 pb-1">
              <SituationLine icon={<Clock size={12} />}>
                {t("Durée des temps d'après les GDH du dossier : {hours}.", { hours: hoursText(m.hours) })}
              </SituationLine>
              <SituationLine icon={<Swords size={12} />}>{t(POSTURE[m.posture])}</SituationLine>
              <SituationLine icon={<Mountain size={12} />}>{mobilityLabel(data.situation)}</SituationLine>
            </div>
          </PanelGroup>

          <MaintenanceBody
            data={data}
            phase={data.phase}
            preview={preview}
            chosen={chosen}
            onChoose={setChosen}
            fleets={
              <FleetTable
                data={data}
                phase={data.phase}
                preview={preview}
                reverts={reverts}
                pending={save.isPending}
                onPreview={(family, changes) => {
                  setChosen(family);
                  setPreview((p) => ({ ...p, ...changes }));
                }}
                onCommit={(changes) => save.mutate(changes)}
              />
            }
            rates={
              <Collapsible title={t("Taux d'avaries et de remises en état (réglables)")} trailing={<AssumptionsBadge data={data} custom={custom} />}>
                <div className="space-y-3">
                  {SLIDERS.map((spec) => (
                    <Slider
                      key={`${spec.key}-${reverts}`}
                      spec={spec}
                      value={data.assumptions[spec.key]}
                      bounds={data.bounds[spec.key]}
                      disabled={save.isPending}
                      onCommit={(value) => save.mutate({ [spec.key]: value })}
                    />
                  ))}
                  <ResetAssumptions data={data} keys={RATE_KEYS} custom={custom} pending={save.isPending} onReset={(v) => save.mutate(v)} />
                </div>
              </Collapsible>
            }
          />

          <PanelGroup flush>
            <PanelRow
              icon={<Fuel size={14} />}
              title={t("Logistique")}
              detail={t("Carburant, munitions et drones : besoins estimés dans leur vue.")}
              state={<ChevronRight size={14} className="text-ink-muted" aria-hidden />}
              onClick={() => setView("logistics")}
              clickTitle={t("Ouvrir la logistique")}
            />
          </PanelGroup>

          <div>
            <Collapsible title={t("Définitions")}>
              <div className="space-y-2 text-justify text-meta leading-relaxed text-ink-soft hyphens-auto">
                <p>
                  <strong className="font-semibold text-ink">{t(CAPOPS_FULL)}</strong>
                  {t(
                    " : aptitude d'une unité à remplir sa mission, en pourcentage de son potentiel initial : la plus faible de trois composantes, le personnel (potentiel de l'unité), les matériels (DTO de l'unité rapportée à sa DTO de T0) et les munitions (d'après les UF consommées). Vert au-dessus de 80 %, orange de 61 à 80 %, rouge à 60 % ou moins.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t(DTO_FULL)}</strong>
                  {t(
                    " : part du parc techniquement disponible (en état de rouler et de combattre), suivie par famille de matériel et par unité. Elle part de la DTO de départ du parc et chute à chaque temps, en proportion de sa durée (au plus sa part d'un engagement de référence de 4 jours) : avaries (taux par jour au contact, au quart hors contact, × 1,25 pour les chars et chenillés, 1,15 pour le génie, 1 pour l'artillerie et les drones, 0,9 pour les blindés à roues, 0,8 pour les véhicules logistiques, majoré jusqu'à × 1,5 par le terrain et la météo), avaries du mouvement (taux pour 100 km parcourus) et, au contact seulement, dommages de combat (taux par jour × intensité du combat), dont un quart sont des pertes définitives.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t("Maintenance opérationnelle")}</strong>
                  {t(
                    " : dépannage et réparation au plus près (NTI1, ELI et TC1), réparation en atelier (NTI2, TC2) et évacuation : chaque jour, elle remet en état {n} % des matériels indisponibles réparables, en continu sur la durée du temps. L'indisponibilité de départ relève de la maintenance industrielle (NTI3, hors du théâtre) et n'est pas remise en état pendant l'exercice.",
                    { n: formatNumber(Math.round(data.assumptions.repairs_per_day)) },
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t("Pertes définitives")}</strong>
                  {t(
                    " : matériels détruits, sortis du parc ; ils ne reviennent pas. La DTO d'une famille est la moyenne de ses unités, pondérée par leurs véhicules.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t("DTO requise par parc")}</strong>
                  {t(
                    " : DTO que chaque parc doit garder à la fin de chaque temps pour la mission (par défaut 70 % en T1, 60 % en T2, 55 % en T3, pour une DTO de départ de 85 %), réglée parc par parc. À la requise ou au-dessus : conforme, jamais une alerte. Dessous : orange de moins de 5 points, rouge au-delà. Avec les taux par défaut, aucun parc ne passe dessous : les taux valent pour un engagement de référence de 4 jours, et une opération plus longue y est ramenée.",
                  )}
                </p>
              </div>
            </Collapsible>

          </div>

          <p className="border-t border-line px-4 py-3 text-justify text-meta leading-relaxed text-ink-muted hyphens-auto">
            {t(
              "Prévision de planification : les taux et les DTO requises sont des ordres de grandeur réglables, pas des valeurs réglementaires. Les avaries et pertes définitives jouées en DIREX s'y ajoutent, au temps de l'incident.",
            )}
          </p>
        </Motion>
      )}
    </FloatingPanel>
  );
}
